import { z } from 'zod'
import type { TypedPocketBase } from '#imports'
export interface CreateCourse extends Create<Collections.Courses> {
  expand: {
    questions: Create<Collections.Questions>[]
  }
}

export const useCourse = () => {
  const { isAdmin } = useUser()
  function goToCourse(slug: string) {
    navigateTo(`/courses/${slug}`)
  }

  function editCourse(id: string) {
    navigateTo(`/admin/course?id=${id}`)
  }

  const schema = z.object({
    title: z.string().min(1, 'Course title is required'),
    subtitle: z.string().optional(),
    description: z.string().min(100, 'Description too short.').max(400, 'Description too long.'),
    rationale: z.string().optional(),
    learning_objectives: z.string().optional(),
    pass_mark: z
      .number()
      .min(0, 'Pass mark cannot be negative')
      .max(100, 'Pass mark cannot exceed 100'),
    added_information: z.string().optional(),
    audience: z.string().optional(),
    image: z.file().or(z.string()).optional(),
    certification: z.string().optional(),
    curriculum: z.string().optional(),
    duration: z.string().min(1, 'Duration is required'),
    format: z.string().optional(),
    is_cpd_accredited: z.boolean().optional(),
    is_published: z.boolean(),
    level: z.string().optional(),
    vat_rate: z.number().optional(),
    slug: z.string(),
    price: z.number().min(0, 'Price cannot be negative'), // This will be handled in the expand section
    expand: z.object({
      questions: z
        .array(
          z.object({
            question_text: z.string().min(1, 'Question text cannot be empty'),
            options: z
              .array(z.string().min(1, 'Option cannot be blank'))
              .min(2, 'At least 2 options required'),
            correct_answer: z.number()
          })
        )
        .min(1, 'Each course needs at least one question')
    })
  })

  const pb = useNuxtApp().$pb as TypedPocketBase

  async function fetchCourseById(id: string) {
    const filter = isAdmin.value ? undefined : 'is_published=true'
    return await pb.collection('courses').getOne(id, { expand: 'questions', filter })
  }

  async function fetchCourseBySlug(slug: string) {
    let filter = `slug="${slug}"`
    if (!isAdmin.value) {
      filter += ' && is_published=true'
    }
    return await pb.collection('courses').getFirstListItem(filter, { expand: 'questions' })
  }

  async function fetchAllCourses() {
    const filter = isAdmin.value ? undefined : 'is_published=true'
    return await pb.collection('courses').getFullList(200, { expand: 'questions', filter })
  }

  async function fetchOtherCourses(excludeId: string, quantity: number = 3) {
    let filter = `id != "${excludeId}"`
    if (!isAdmin.value) {
      filter += ' && is_published=true'
    }
    const courses = await pb.collection('courses').getList(1, quantity, {
      filter,
      expand: 'questions'
    })
    return courses.items
  }

  async function createCourse(data: CreateCourse) {
    // First create the course without questions
    const { expand, ...courseData } = data

    for (const question of expand.questions) {
      const savedQuestion = await pb.collection('questions').create(question)
      courseData.questions = [...(courseData.questions || []), savedQuestion.id]
    }
    const createdCourse = await pb.collection('courses').create(courseData)
    return createdCourse
  }

  async function updateCourse(data: CreateCourse) {
    if (!data.id) {
      throw new Error('Course id is required for updates')
    }

    const { expand, ...courseData } = data

    for (const question of expand.questions) {
      if (!question.id) {
        // New question, create it
        const savedQuestion = await pb.collection('questions').create(question)
        courseData.questions = [...(courseData.questions || []), savedQuestion.id]
      } else {
        // Existing question, update it
        await pb.collection('questions').update(question.id, question)
      }
    }
    const updatedCourse = await pb.collection('courses').update(data.id, courseData)
    return updatedCourse
  }

  async function useHasAccessToFullCourse(courseId: string) {
    const { isAdmin, isLoggedIn, userId } = useUser()
    if (!isLoggedIn.value) return false
    if (isAdmin.value) return true
    // Check if the user has access to the course (e.g., through purchases or enrollments)
    const hasAccess = await pb
      .collection('enrollments')
      .getFirstListItem(`user="${userId.value}" && course="${courseId}"`)
    return !!hasAccess
  }

  return {
    goToCourse,
    editCourse,
    fetchOtherCourses,
    schema,
    fetchCourseById,
    fetchCourseBySlug,
    fetchAllCourses,
    createCourse,
    updateCourse,
    useHasAccessToFullCourse
  }
}
